import { Mutation, Typenames } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import OtpScreen from 'features/otp/OtpScreen';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ChangePasswordInitiate from 'services/graphql/mutations/ChangePasswordInitiate';
import ChangePasswordVerifyOtp from 'services/graphql/mutations/ChangePasswordVerifyOtp';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useMutation } from 'urql';

const VERIFY_SCREEN_ERROR_CODES = [Typenames.ChangePasswordTooManyAttempts];

export const VerifyPhoneNumber = () => {
  const router = useRouter();
  const { t: translatedTextOtp } = useTranslation('otp');
  const { t: translatedTextOtpError } = useTranslation('otp', {
    keyPrefix: 'otpError',
  });
  const { lastFourPhoneDigits, password } = useContext(MyVaroContext);
  const [{ fetching: verifyFetching }, executeVerifyMutation] =
    useMutation<Mutation>(ChangePasswordVerifyOtp);
  const [{ fetching: resendFetching }, executeResendMutation] =
    useMutation<Mutation>(ChangePasswordInitiate);

  const otpSuccessEvent = usePressableAnalytics({
    analyticsId: 'verificationSuccessful',
    customAttributes: {
      verification_type: 'phone',
    },
  });
  const handleVerifyOtp = async (code: string) => {
    try {
      const response = await executeVerifyMutation({
        input: { otp: code },
      });

      const verifyResponse = response.data?.changePasswordVerifyOtp;

      // Successful verification
      if (
        verifyResponse?.__typename === Typenames.ChangePasswordVerifyOtpSuccess
      ) {
        otpSuccessEvent();
        router.navigate(
          '/myvaro/personal-details/change-password/confirm-password',
        );

        return { success: true };
      }

      // Incorrect OTP (input-level error)
      if (verifyResponse?.__typename === Typenames.ChangePasswordIncorrectOtp) {
        return {
          errorCode: Typenames.ChangePasswordIncorrectOtp,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      // Expired OTP (input-level error)
      if (verifyResponse?.__typename === Typenames.ChangePasswordExpiredOtp) {
        return {
          errorCode: Typenames.ChangePasswordExpiredOtp,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      if (
        verifyResponse?.__typename ===
        Typenames.ChangePasswordTooManyOtpAttempts
      ) {
        return {
          errorCode: verifyResponse.code,
          title: verifyResponse.title,
          message: verifyResponse.message,
        };
      }

      // Generic catch all error (screen-level error)
      return {
        success: false,
        message: translatedTextOtpError('tryAgainMessage'),
      };
    } catch (error) {
      // Generic catch all error (screen-level error)
      logErrorWithContext(
        'My Varo Change Password verify OTP error: ' + error,
        {
          featureArea: FeatureArea.AUTHENTICATION,
          responsibleTeam: ResponsibleTeam.FRAUD,
          severity: ErrorSeverity.HIGH,
        },
      );
      return {
        success: false,
        message: translatedTextOtp('genericError'),
      };
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await executeResendMutation({
        input: { currentPassword: password },
      });

      const resendResponse = response.data?.changePasswordInitiate;

      // Successful resend
      if (
        resendResponse?.__typename === Typenames.ChangePasswordInitiateSuccess
      ) {
        return {
          success: true,
        };
      }

      // Too many attempts screen-level error
      if (
        resendResponse?.__typename === Typenames.ChangePasswordTooManyAttempts
      ) {
        return {
          errorCode: String(Typenames.ChangePasswordTooManyAttempts),
          title: resendResponse.title,
          message: resendResponse.message,
        };
      }

      // Handle error case
      return {
        errorCode: 500,
        message: translatedTextOtpError('codeFiledToSend'),
      };
    } catch (error) {
      logErrorWithContext(
        'My Varo Change Password resend OTP error: ' + error,
        {
          featureArea: FeatureArea.AUTHENTICATION,
          responsibleTeam: ResponsibleTeam.FRAUD,
          severity: ErrorSeverity.HIGH,
        },
      );

      return {
        errorCode: 500,
        message: translatedTextOtp('genericError'),
      };
    }
  };

  const description = `${translatedTextOtp('common.description')}${String(lastFourPhoneDigits)}.`;

  return (
    <OtpScreen
      flowKey={OtpFlowKey.PROFILE_CHANGE}
      verificationType={OtpVerificationType.PHONE}
      // Text content
      description={description}
      // External state
      isLoading={verifyFetching || resendFetching}
      // Configurable error codes
      screenLevelErrorCodes={VERIFY_SCREEN_ERROR_CODES}
      // Event handlers
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={() => router.back()}
    />
  );
};

export default VerifyPhoneNumber;
